import _ from 'lodash';
import type { z } from 'zod';
import { type Subject } from '../common/enums';
import { SearchStringSchema } from '../common/schemas';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { VideoAttributes } from './constants';
import { DBVideoSchema, VideoSchema } from './schemas';

type DBVideo = z.infer<typeof DBVideoSchema>;
type Video = z.infer<typeof VideoSchema>;

export const VideoCodecConfig: TransformerConfig<typeof VideoSchema, typeof DBVideoSchema> = {
  name: 'Video',
  clientSchema: VideoSchema,
  dbSchema: DBVideoSchema,
  transformers: [
    // topic: { subject, category, topic } ↔ topic: 'subject#category#topic'
    createTransformer<typeof VideoSchema, typeof DBVideoSchema>({
      encoder: {
        sourceFields: ['topic'] as const,
        encode: ({ topic }) => ({
          topic: [
            (topic as Video['topic']).subject,
            (topic as Video['topic']).category,
            (topic as Video['topic']).topic,
          ]
            .filter(x => x)
            .join('#'),
        }),
      },
      decoder: {
        sourceFields: ['topic'] as const,
        decode: ({ topic }) => {
          const parts = (topic as DBVideo['topic']).split('#');
          return {
            topic: {
              subject: parts[0] as Subject,
              category: parts[1] as string,
              topic: parts[2] as string,
            },
          };
        },
      },
    }),

    // Handle search string updates
    createTransformer<typeof VideoSchema, typeof DBVideoSchema>({
      encoder: {
        sourceFields: VideoAttributes.Search,
        dependencies: VideoAttributes.Search,
        encode: video => {
          let searchStr = '';
          VideoAttributes.Search.forEach(attr => {
            const val = video[attr];
            searchStr += SearchStringSchema.parse(val);
          });
          return { searchStr, ..._.pick(video, VideoAttributes.Search) };
        },
      },
    }),
  ],
};

export const encodeVideoToDb = createEncodeToDbFn(VideoCodecConfig);
export const decodeVideoFromDb = createDecodeFromDbFn(VideoCodecConfig);
